<div class="card shadow-sm">
  <div class="card-header d-flex justify-content-between align-items-start border-bottom pb-3">
    <div class="d-flex align-items-start gap-3 flex-grow-1">
      <div class="import-icon-wrap" [ngClass]="source === ImportSource.CA ? 'import-icon-wrap--human' : 'import-icon-wrap--ai'">
        <i [class]="iconClass"></i>
      </div>
      <div
        class="flex-grow-1 collapse-header-content"
        (click)="toggleCollapse()"
        [attr.aria-expanded]="!isCollapsed"
        role="button"
        tabindex="0"
        (keydown.enter)="toggleCollapse()"
        (keydown.space)="toggleCollapse(); $event.preventDefault()"
        placement="top"
      >
        <h5 class="mb-1 fw-bold" [ngClass]="source === ImportSource.CA ? 'import-title--human' : 'import-title--ai'">{{ title }}</h5>
        <p class="text-muted mb-0">{{ 'Settings::DataImport:Description' | abpLocalization }}</p>
      </div>
    </div>
    <span [class]="statusBadgeClass" class="status-badge-modern">
      <span class="status-badge-dot"></span>
      <span class="status-badge-text">{{ statusTextKey | abpLocalization }}</span>
    </span>
  </div>

  <div class="card-body d-flex flex-column" [class.collapsed]="isCollapsed">
    <app-info-banner
      *ngIf="!state.systemStartDate"
      class="mb-3"
      mode="warning"
      [description]="'Settings::DataImport:SystemStartDateNotAvailable' | abpLocalization">
    </app-info-banner>
    <div class="d-flex justify-content-center mb-3">
      <div class="progress-circle-wrapper position-relative">
        <app-progress-circle
          [progress]="state.progress"
          [status]="state.status"
          (click)="onProgressToggle($event)"
        ></app-progress-circle>

        <div
          class="tooltip-backdrop"
          *ngIf="isProgressTooltipVisible"
          (click)="onTooltipClose($event)"
        ></div>

        <div
          class="import-progress-tooltip card shadow-sm"
          *ngIf="isProgressTooltipVisible"
        >
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="fw-bold small">
                {{ 'Settings::DataImport:ProgressByMetric' | abpLocalization }}
              </div>
              <button
                type="button"
                class="btn-close import-progress-tooltip-close"
                aria-label="Close"
                (click)="onTooltipClose($event)"
              ></button>
            </div>
            <div *ngFor="let detail of state.progressDetails; trackBy: trackByMetricId" class="mb-2">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <div class="d-flex align-items-center gap-1">
                  <span class="small text-muted">{{ detail.metricName | abpLocalization }}</span>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 metric-info-btn"
                    (click)="onMetricInfoClick(detail, $event)"
                    [attr.aria-expanded]="isMetricDetailVisible && selectedMetricId === detail.metricId"
                    [attr.aria-label]="'Settings::DataImport:MetricDetail:ShowDetails' | abpLocalization"
                  >
                    <i class="fas fa-info-circle"></i>
                  </button>
                </div>
                <span class="small fw-bold">{{ detail.progress }}%</span>
              </div>
              <div class="progress">
                <div
                  class="progress-bar"
                  [ngClass]="getProgressBarClass(detail)"
                  role="progressbar"
                  [style.width.%]="detail.progress"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <app-import-info-card
      [timeRemaining]="state.timeRemaining"
      [status]="state.status"
      [errorCount]="state.errorCount"
      [systemStartDate]="state.systemStartDate"
      [lastImport]="state.lastImport"
      (errorClick)="onErrorClick()"
    ></app-import-info-card>

    <app-import-scope
      [systemStartDate]="systemStartDate"
      [source]="source"
      [initialScopeType]="initialScopeType"
      (scopeChange)="onScopeChange($event)"
    ></app-import-scope>

    <div class="mt-3 pt-3 d-flex justify-content-end">
      <button
        *ngIf="canStopImport"
        class="btn btn-outline-danger btn-sm"
        (click)="onStopImport()"
      >
        {{ 'Settings::DataImport:StopImport' | abpLocalization }}
      </button>

      <div
        *ngIf="!canStopImport"
        class="d-inline-block"
        [class.disabled-button-wrapper]="!canStartImport"
        [pTooltip]="!canStartImport ? (buttonDisabledTooltip | abpLocalization) : null"
        tooltipPosition="top"
      >
        <button
          class="btn btn-primary btn-sm"
          [disabled]="!canStartImport"
          (click)="onStartImport()"
        >
          <span *ngIf="isStarting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <span *ngIf="!isStarting">{{ 'Settings::DataImport:StartImport' | abpLocalization }}</span>
          <span *ngIf="isStarting">{{ 'Settings::DataImport:StartingImport' | abpLocalization }}</span>
        </button>
      </div>
    </div>
  </div>

  <p-popover
    #metricDetailPopover
    appendTo="body"
    (onShow)="onMetricDetailShow()"
    (onHide)="onMetricDetailHide()"
  >
    <div class="metric-detail-panel" *ngIf="selectedMetricDetail as detail">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="fw-bold small">{{ detail.metricName | abpLocalization }}</div>
        <button
          type="button"
          class="btn-close metric-detail-close"
          aria-label="Close"
          (click)="metricDetailPopover.hide()"
        ></button>
      </div>
      <div class="metric-detail-content">
        <div class="metric-detail-row">
          <span class="metric-detail-label text-muted small">
            {{ 'Settings::DataImport:MetricDetail:TotalRecords' | abpLocalization }}
          </span>
          <span class="metric-detail-value fw-bold">{{ detail.totalRecords }}</span>
        </div>
        <div class="metric-detail-row">
          <span class="metric-detail-label text-muted small">
            {{ 'Settings::DataImport:MetricDetail:CompletedRecords' | abpLocalization }}
          </span>
          <span class="metric-detail-value fw-bold text-success">{{ detail.completedRecords }}</span>
        </div>
        <div class="metric-detail-row">
          <span class="metric-detail-label text-muted small">
            {{ 'Settings::DataImport:MetricDetail:ErrorRecords' | abpLocalization }}
          </span>
          <span class="metric-detail-value fw-bold text-danger">{{ detail.errorRecords }}</span>
        </div>
      </div>
    </div>
  </p-popover>
</div>
